<script setup lang="ts">
import { useDrawingStore } from "@/stores/fileStore";
import { storeToRefs } from "pinia";
import UploadItem from "@/components/files/UploadList/UploadItem.vue";
import { computed } from "vue";

const { trackers } = storeToRefs(useDrawingStore());
const { removeTracker } = useDrawingStore();

const activeTrackers = computed(() => {
  return trackers.value;
});

const globalStatus = computed(() => {
  if (
    activeTrackers.value.length === 0 ||
    activeTrackers.value.every(({ status }) => status === "done")
  ) {
    return "done";
  }
  if (activeTrackers.value.every(({ status }) => status === "failed")) {
    return "failed";
  }
  return "loading";
});
</script>

<template>
  <div
    v-if="!!activeTrackers.length"
    class="upload-toast p-3 shadow-lg rounded border border-1"
  >
    <div class="d-flex gap-4 align-items-start">
      <div
        v-if="globalStatus === 'failed'"
        class="mt-1 position-relative d-flex align-items-center justify-content-center bg-light rounded-circle p-2 border border-3 border-danger"
      >
        <el-icon size="1.5rem"><i class="bi-x-lg text-danger"></i></el-icon>
      </div>
      <div
        v-else-if="globalStatus === 'done'"
        class="mt-1 position-relative d-flex align-items-center justify-content-center bg-light rounded-circle p-2 border border-3 border-success"
      >
        <el-icon size="1.5rem"
          ><i class="bi-check-lg text-success"></i
        ></el-icon>
      </div>
      <div
        v-else-if="globalStatus === 'loading'"
        class="mt-1 position-relative d-flex align-items-center justify-content-center bg-light rounded-circle p-2"
      >
        <el-icon size="1.5rem"><i class="bi-cloud-arrow-up"></i></el-icon>
        <div
          class="spinner-border text-primary position-absolute"
          style="width: 3rem; height: 3rem"
          role="status"
        ></div>
      </div>
      <div class="d-flex flex-column gap-1">
        <span class="fw-bold"> {{ $t("dashboard:loading.in.progress") }} </span>
        <span style="font-size: 0.8rem" class="text-secondary">{{
          $t("dashboard:transfer.drawing.message")
        }}</span>
      </div>
    </div>
    <el-collapse class="mt-3">
      <el-collapse-item>
        <template #title>
          <span class="fw-normal" style="font-size: 0.8rem">{{
            `${$t("details")} (${activeTrackers.length} ${
              activeTrackers.length > 1 ? $t("files") : $t("file")
            })`
          }}</span>
        </template>
        <div class="d-flex">
          <TransitionGroup
            tag="ul"
            id="files-list"
            class="list-group w-100"
            name="fade"
          >
            <UploadItem
              v-for="tracker in activeTrackers"
              :tracker="tracker"
              :key="tracker.id"
              @remove="() => removeTracker(tracker.id)"
            />
          </TransitionGroup>
        </div>
      </el-collapse-item>
    </el-collapse>
  </div>
</template>

<style lang="scss">
.upload-toast {
  background-color: var(--el-bg-color);
  border-color: var(--bs-border-color) !important;
  color: var(--bs-body-color);

  .el-collapse {
    border-bottom: 0 !important;
    border-top: 0 !important;
    --el-collapse-header-bg-color: transparent;
    --el-collapse-content-bg-color: transparent;
  }

  .el-collapse-item__wrap {
    border-bottom: 0 !important;
    background-color: transparent;
  }

  .el-collapse-item__header {
    border-bottom: 0 !important;
    height: 1.8rem !important;
    background-color: transparent;
    color: var(--bs-body-color);
  }

  .el-collapse-item__content {
    background-color: transparent;
    color: var(--bs-body-color);
  }

  .el-collapse-item__arrow {
    color: var(--bs-body-color);
  }
}
</style>
